import { apiSlice } from "features/api/apiSlice";
import { RESULTS_PER_PAGE } from "constants";
import { createEntityAdapter } from "@reduxjs/toolkit";
import { formatDate } from "utils/helpers";

const queueAdapter = createEntityAdapter({});

const initialState = queueAdapter.getInitialState({ size: 0 });

export const callCenterQueueTiers = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getQueueAgents: builder.query({
      query: (id, searchParams) =>
        `/call_center/queues/${id}/agents?${searchParams}&limit=${RESULTS_PER_PAGE}`,

      transformResponse: (response) => {
        // console.log("Raw API Response:", response);

        const fetchedQueueAgents = response.results.map((queueAgent) => {
          const {
            name,
            type,
            contact,
            status,
            domain,
            no_answer_delay_time,
            max_no_answer,
            wrap_up_time,
            reject_delay_time,
            busy_delay_time,
            domain_id,
            enabled,
            created_at,
            updated_at,
            ...queueAgentProps
          } = queueAgent;

          queueAgentProps["enabled"] = enabled ? "Yes" : "No";

          return {
            name,
            type,
            contact,
            domain_name: domain?.name || "Unknown",
            ...queueAgentProps,
            created_at: formatDate(created_at),
          };
        });

        return queueAdapter.setAll(
          { ...initialState, size: fetchedQueueAgents.length },
          fetchedQueueAgents
        );
      },

      providesTags: (result) =>
        result
          ? [
            ...result.ids.map((id) => ({ type: "QueueAgents", id })),
            { type: "QueueAgents", id: "PARTIAL-LIST" },
          ]
          : [{ type: "QueueAgents", id: "PARTIAL-LIST" }],
    }),
    createQueueTier: builder.mutation({
      query: ({ id, data }) => {
        // console.log("API Call - ID:", id, "Data:", data);
        return {
          url: `/call_center/queues/${id}/tiers`,
          method: "POST",
          body: JSON.stringify(data),
        };
      },
      invalidatesTags: [{ type: "QueueTiers", id: "PARTIAL-LIST" }],
    }),
    getQueueTiers: builder.query({
      query: ({ id, searchParams }) =>
        `/call_center/queues/${id}/tiers?${searchParams}&limit=${RESULTS_PER_PAGE}`,

      transformResponse: (response) => {
        // console.log("Raw API Response:", response);

        const fetchedQueueTiers = response.results.map((queueTier) => {
          const {
            domain,
            agent,
            domain_id,
            queue_id,
            agent_id,
            enabled,
            created_at,
            updated_at,
            ...queueTierProps
          } = queueTier;

          queueTierProps["enabled"] = enabled ? "Yes" : "No";

          return {
            agent_name: agent?.name || "Unknown",
            domain_name: domain?.name || "Unknown",
            ...queueTierProps,
            created_at: formatDate(created_at),
          };
        });

        return queueAdapter.setAll(
          { ...initialState, size: fetchedQueueTiers.length },
          fetchedQueueTiers
        );
      },

      providesTags: (results) =>
        results
          ? [
            ...results.ids.map((id) => ({ type: "QueueTiers", id })),
            { type: "QueueTiers", id: "PARTIAL-LIST" },
          ]
          : [{ type: "QueueTiers", id: "PARTIAL-LIST" }],
    }),
    getQueueTierById: builder.query({
      query: ({ queueId, tierId }) => `/call_center/queues/${queueId}/tiers/${tierId}`,
      providesTags: (result, error, id) => [
        { type: "QueueTiers", id },
      ],
    }),
    updateQueueTier: builder.mutation({
      query: ({ queueId, tierId, ...data }) => ({
        url: `/call_center/queues/${queueId}/tiers/${tierId}`,
        method: "PATCH",
        body: JSON.stringify(data),
      }),
      invalidatesTags: (result, error, id) => [
        { type: "QueueTiers", id },
      ],
    }),
    deleteQueueTier: builder.mutation({
      query: ({ queueId, tierId }) => ({
        url: `/call_center/queues/${queueId}/tiers/${tierId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { tierId }) => [
        { type: "QueueTiers", id: tierId },
        { type: "QueueTiers", id: "PARTIAL-LIST" },
      ],
    }),
  }),
});

export const {
  useGetQueueAgentsQuery,
  useCreateQueueTierMutation,
  useGetQueueTiersQuery,
  useGetQueueTierByIdQuery,
  useUpdateQueueTierMutation,
  useDeleteQueueTierMutation,
} = callCenterQueueTiers;
